import { onMounted, onServerPrefetch, useSSRContext } from 'vue'

import { useConfigStore, type RsConfig } from '../stores/config'
import { usePageStore } from '../stores/page'
import { useAdaStore, type AdSizePrediction } from '../stores/ada'
import { useSourcepointStore } from '../stores/sourcepoint'
import { useSessionStore } from '../stores/session'
import { useAdditionalConsent } from './use-additional-consent'

import type { Brand } from '@xcel-next/core/enums'
import type { ConsentConfig } from '@xcel-next/types'
import type { FetchPageContext } from '../types'

export type DefaultAppInitProps = {
  brand: Brand,
  url: string,
  authorization: string | null,
  consentConfig: ConsentConfig | null,
  adSizePrediction: AdSizePrediction | null,
  adDefendScript: string | null,
  crawlerDetected: boolean,
  config: RsConfig
}

export const defaultAppInitProps = {
  brand: { type: String, default: '' },
  url: { type: String, default: '' },
  authorization: { type: String, default: null },
  consentConfig: { type: Object, default: () => ({}) },
  adSizePrediction: { type: Object, default: () => ({}) },
  adDefendScript: { type: String, default: '' },
  crawlerDetected: { type: Boolean, default: false },
  config: { type: Object, default: () => ({}) }
}

export function useDefaultAppInit (props: DefaultAppInitProps): void {
  const configStore = useConfigStore()
  const pageStore = usePageStore()
  const adaStore = useAdaStore()
  const sourcepointStore = useSourcepointStore()
  const sessionStore = useSessionStore()
  const { loadAdditionalConsent } = useAdditionalConsent()

  onMounted(async () => {
    // seed the local (PUR) embed opt-in from its cookie into reactive state
    loadAdditionalConsent()
    await pageStore.appMounted()
  })

  onServerPrefetch(async () => {
    sourcepointStore.setConsentConfig(props.consentConfig)
    configStore.setRsConfig(props.config)
    adaStore.setAdSizePrediction(props.adSizePrediction)
    adaStore.setAdDefendScript(props.adDefendScript)

    const ssrContext = useSSRContext<{ Authorization?: string }>()
    const authz = props.authorization || ssrContext?.Authorization

    if (authz) {
      sessionStore.setToken(authz)
    }

    const context: FetchPageContext = { url: props.url, brand: props.brand, rsConfig: configStore.rsConfig as unknown as RsConfig, crawlerDetected: props.crawlerDetected }
    return pageStore.fetchPage(context)
  })
}
